<template>
  <div v-if="loading" class="flex justify-center py-8">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
  </div>
  <div v-else-if="error" class="text-center text-danger-500 py-6">
    {{ error }}
  </div>
  <div v-else-if="!order" class="text-center py-6">
    {{ directT('ecommerce.orderDetails.orderNotFound') }}
  </div>
  <div v-else>
    <div class="lg:grid lg:grid-cols-3 gap-[25px]">
      <div class="lg:col-span-2">
        <TrackingID :order="order" />
        <RecentOrders :orderId="orderId" :order="order" />
      </div>

      <div class="lg:col-span-1">
        <OrderSummary :order="order" />
      </div>
    </div>
    <div
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[25px] mb-[25px]"
    >
      <BillingDetails :order="order" />
      <ShippingDetails :order="order" />
      <DeliveryDetails :order="order" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, PropType, onMounted, watch } from "vue";
import TrackingID from "./TrackingID.vue";
import RecentOrders from "./RecentOrders.vue";
import OrderSummary from "./OrderSummary.vue";
import BillingDetails from "./BillingDetails.vue";
import ShippingDetails from "./ShippingDetails.vue";
import DeliveryDetails from "./DeliveryDetails.vue";
import { getOrderById, Order } from "@/services/orderService";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

export default defineComponent({
  name: "OrderDetailsComponent",
  components: {
    TrackingID,
    RecentOrders,
    OrderSummary,
    BillingDetails,
    ShippingDetails,
    DeliveryDetails,
  },
  props: {
    orderId: {
      type: String as PropType<string>,
      required: false,
      default: ''
    }
  },
  setup(props) {
    // State
    const order = ref<Order | null>(null);
    const loading = ref(true);
    const error = ref<string | null>(null);
    
    // i18n setup
    const { locale } = useI18n();

    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };

    // Fetch order data
    const fetchOrder = async () => {
      if (!props.orderId) {
        error.value = directT('ecommerce.orderDetails.noOrderIdProvided');
        loading.value = false;
        return;
      }

      loading.value = true;
      error.value = null;

      try {
        const { order: fetchedOrder, error: fetchError } = await getOrderById(props.orderId);
        
        if (fetchError) {
          error.value = fetchError;
        } else if (!fetchedOrder) {
          error.value = directT('ecommerce.orderDetails.orderNotFound');
        } else {
          order.value = fetchedOrder;
        }
      } catch (e) {
        error.value = e instanceof Error ? e.message : directT('ecommerce.orderDetails.errorFetchingOrder');
      } finally {
        loading.value = false;
      }
    };

    // Watch for orderId changes
    watch(() => props.orderId, (newId) => {
      if (newId) {
        fetchOrder();
      }
    });

    // Initial fetch
    onMounted(() => {
      fetchOrder();
    });

    return {
      order,
      loading,
      error,
      directT
    };
  }
});
</script>
